import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { fmtDate, fmtNum } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const STAGE = {
  REQUESTED: { label: "Requested", cls: "bg-slate-500/15 text-slate-400" },
  ORDERED: { label: "Ordered", cls: "bg-sky-500/15 text-sky-400" },
  MEASURED: { label: "Measured", cls: "bg-indigo-500/15 text-indigo-400" },
  BILLED: { label: "Billed", cls: "bg-amber-500/15 text-amber-400" },
  PART_PAID: { label: "Part paid", cls: "bg-orange-500/15 text-orange-400" },
  PAID: { label: "Paid", cls: "bg-emerald-500/15 text-emerald-400" },
};
const qty = (v, unit) => `${fmtNum(v, 2)}${unit ? ` ${unit}` : ""}`;
const inr = (v) => `₹${fmtNum(v, 2)}`;

function StagePill({ stage }) {
  const s = STAGE[stage] || STAGE.REQUESTED;
  return <span className={`inline-block rounded-md px-2 py-0.5 text-xs font-semibold ${s.cls}`} data-testid={`stage-${stage}`}>{s.label}</span>;
}

/** Plan / Buy workspace: each activity's subcontract work from service requisition to payment. */
export default function ActivityTracker() {
  const { projectId } = useProjectCtx();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState({});
  const [q, setQ] = useState("");

  useEffect(() => {
    let live = true;
    api.get(`/projects/${projectId}/activity-tracker`)
      .then((r) => { if (live) { setData(r.data); setFailed(false); } })
      .catch((err) => { if (live) { setFailed(true); toast.error(getErrorMessage(err)); } });
    return () => { live = false; };
  }, [projectId]);

  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (data?.rows || []).filter((r) => !t || `${r.code || ""} ${r.name} ${r.vendors}`.toLowerCase().includes(t));
  }, [data, q]);

  return (
    <div className="space-y-6" data-testid="activity-tracker-page">
      <PageHeader title="Activity Tracker" description="Follow each activity's subcontract work from the service requisition to the payment: requested, ordered, measured, billed and paid." />
      {failed ? (
        <EmptyState title="Could not load the tracker" description="Try again in a moment." />
      ) : !data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : data.rows.length === 0 ? (
        <EmptyState title="Nothing to track yet" description="Pick an activity on a service requisition and its progress will appear here." testId="activity-tracker-empty" />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="activity-tracker-totals">
            {[["Ordered", data.totals.ordered_value], ["Billed", data.totals.billed_value], ["Paid", data.totals.paid_value], ["Still to pay", data.totals.unpaid_value]].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-border p-3">
                <div className="text-xs font-semibold text-muted-foreground">{label}</div>
                <div className="mt-1 font-mono text-lg font-semibold text-foreground">{inr(value)}</div>
              </div>
            ))}
          </div>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search activity or vendor…" className="max-w-sm" data-testid="activity-tracker-search" />
          <div className="rounded-xl border border-border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">No.</TableHead><TableHead>Activity</TableHead><TableHead>Stage</TableHead>
                  <TableHead className="text-right">Requested</TableHead><TableHead className="text-right">Ordered</TableHead>
                  <TableHead className="text-right">Measured</TableHead><TableHead className="text-right">Billed ₹</TableHead>
                  <TableHead className="text-right">Paid ₹</TableHead><TableHead className="text-right">To pay ₹</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const key = r.wbs_item_id || "none";
                  const expanded = !!open[key];
                  return (
                    <Fragment key={key}>
                      <TableRow className="cursor-pointer hover:bg-muted/30" onClick={() => setOpen((o) => ({ ...o, [key]: !expanded }))} data-testid={`activity-row-${key}`}>
                        <TableCell className="font-mono text-xs text-muted-foreground"><span className="inline-flex items-center gap-1">{expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}{r.code || ""}</span></TableCell>
                        <TableCell>
                          <div className="font-semibold text-foreground">{r.name}</div>
                          {r.vendors && <div className="text-xs text-muted-foreground">{r.vendors}</div>}
                        </TableCell>
                        <TableCell><StagePill stage={r.stage} /></TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {qty(r.requested_qty, r.unit)}
                          {Number(r.to_order_qty) > 0 && <div className="text-[10px] text-amber-400">{fmtNum(r.to_order_qty, 2)} not ordered</div>}
                          {r.unit_warning && <span title="Lines use different units" className="ml-1 inline-block align-middle text-amber-400"><AlertTriangle className="h-3 w-3" /></span>}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">{qty(r.ordered_qty, r.unit)}<div className="text-[10px] text-muted-foreground">{inr(r.ordered_value)}</div></TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {qty(r.measured_qty, r.unit)}
                          {Number(r.to_measure_qty) > 0 && <div className="text-[10px] text-muted-foreground">{fmtNum(r.to_measure_qty, 2)} to measure</div>}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {inr(r.billed_value)}
                          {Number(r.to_bill_qty) > 0 && <div className="text-[10px] text-amber-400">{fmtNum(r.to_bill_qty, 2)} not billed</div>}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">{inr(r.paid_value)}</TableCell>
                        <TableCell className="text-right font-mono text-xs font-semibold">{inr(r.unpaid_value)}</TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow className="bg-muted/10">
                          <TableCell />
                          <TableCell colSpan={8}>
                            <ul className="space-y-1 py-1 text-xs" data-testid={`activity-docs-${key}`}>
                              {r.docs.map((d, i) => (
                                <li key={i} className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                  <span className="w-36 text-muted-foreground">{d.type}</span>
                                  <Link to={d.link} className="font-mono text-primary hover:underline" onClick={(e) => e.stopPropagation()}>{d.number}</Link>
                                  <span className="text-muted-foreground">{fmtDate(d.date)}</span>
                                  <span className="font-mono">{fmtNum(d.qty, 2)}{r.unit ? ` ${r.unit}` : ""}</span>
                                  {d.amount && <span className="font-mono">{inr(d.amount)}</span>}
                                  <span className="text-muted-foreground">{d.status}</span>
                                </li>
                              ))}
                            </ul>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
